import { useEffect, useRef } from 'react';
import { Box, Card, Title } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { AiFeature, Domain } from '@tiendanube/common/src/enums';
import ModalAside from 'App/components/lab/ModalAside';
import { CATALOG_PRODUCT_HASHED_SLUG } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { LanguageInterface, LanguageInterfaceBoolean } from 'App/i18n';
import GenerateWithAiLink from 'commons/components/GenerateWithAiLink';
import { useAiField, useAiGeneratedValues, useModal } from 'commons/hooks';
import { AI_FIELDS } from 'commons/services/AgentChatService/AgentChatActionBusTypes';
import { mergeObjectsWithContent } from 'commons/utils/mergeObjectsWithContent';
import {
  trackingAiGenerateBrandEdit,
  trackingAiGenerateSeoDescriptionEdit,
  trackingAiGenerateSeoTitleEdit,
  trackingAiGenerateTagsEdit,
} from 'domains/Ai/tracking';
import { Brand, Seo, AllTags, ShowTagsAndSeoCard } from './components';
import { onChangeBrandType } from './components/Brand/Brand';
import { onChangeSeoType } from './components/Seo/FormSeo/FormSeo';
import { SeoProps } from './components/Seo/Seo';
import { useTagsAndSeoIA } from './hooks/useTagsAndSeoAI';

export type TagsType = string[];

export type onChangeTagsType = ({
  name,
  value,
}: {
  name: string;
  value: TagsType;
}) => void;

interface TagsAndSeoProps
  extends Omit<
    SeoProps,
    | 'seoTitleAI'
    | 'seoDescriptionAI'
    | 'missingNameAndDescription'
    | 'onClickGenerateWithAI'
  > {
  tags: string[];
  onChangeBrand: onChangeBrandType;
  onChangeSeo: onChangeSeoType;
  onChangeTags: onChangeTagsType;
  brand: string;
  autoOpen?: boolean;
}

function TagsAndSeo({
  tags,
  onChangeBrand,
  brand,
  name,
  description,
  seoDescription,
  seoTitle,
  onChangeSeo,
  onChangeTags,
  seoUrl,
  autoOpen = false,
}: Readonly<TagsAndSeoProps>): JSX.Element {
  const { t } = useTranslation(Domain.CATALOG);
  const [hasCatalogProductHashedSlug] = useHasTags([
    CATALOG_PRODUCT_HASHED_SLUG,
  ]);
  const [isShowModal, openModal, closeModal] = useModal(false);

  const isSuggestingTagsDismissAI = useRef(false);

  const hasAutoOpenedRef = useRef(false);

  const seoTitleField = useAiField<LanguageInterface>({
    componentId: AI_FIELDS.PRODUCT_SEO_TITLE,
    getValue: () => seoTitle,
    onReplace: (aiGeneratedValue: LanguageInterface) => {
      onChangeSeo({
        name: 'seoTitle',
        value: {
          ...seoTitle,
          ...aiGeneratedValue,
        },
      });
    },
    onAdd: (aiGeneratedValue: LanguageInterface) => {
      onChangeSeo({
        name: 'seoTitle',
        value: mergeObjectsWithContent(seoTitle, aiGeneratedValue),
      });
    },
  });

  const seoDescriptionField = useAiField<LanguageInterface>({
    componentId: AI_FIELDS.PRODUCT_SEO_DESCRIPTION,
    getValue: () => seoDescription,
    onReplace: (aiGeneratedValue: LanguageInterface) => {
      onChangeSeo({
        name: 'seoDescription',
        value: {
          ...seoDescription,
          ...aiGeneratedValue,
        },
      });
    },
    onAdd: (aiGeneratedValue: LanguageInterface) => {
      onChangeSeo({
        name: 'seoDescription',
        value: mergeObjectsWithContent(seoDescription, aiGeneratedValue),
      });
    },
  });

  const seoUrlField = useAiField<LanguageInterface>({
    componentId: AI_FIELDS.PRODUCT_SEO_URL,
    getValue: () => seoUrl,
    onReplace: (aiGeneratedValue: LanguageInterface) => {
      onChangeSeo({
        name: 'seoUrl',
        value: {
          ...seoUrl,
          ...aiGeneratedValue,
        },
      });
    },
    onAdd: (aiGeneratedValue: LanguageInterface) => {
      onChangeSeo({
        name: 'seoUrl',
        value: mergeObjectsWithContent(seoUrl, aiGeneratedValue),
      });
    },
  });

  const tagsField = useAiField<string[]>({
    componentId: AI_FIELDS.PRODUCT_TAGS,
    getValue: () => tags,
    onReplace: (value) => {
      onChangeTags({ name: 'tags', value });
    },
    onAdd: (value) => {
      onChangeTags({ name: 'tags', value: [...tags, ...value] });
    },
    onRemove: (value) => {
      onChangeTags({
        name: 'tags',
        value: tags.filter((tag) => !value.includes(tag)),
      });
    },
    onClear: () => {
      onChangeTags({ name: 'tags', value: [] });
    },
  });

  const brandField = useAiField<string>({
    componentId: AI_FIELDS.PRODUCT_BRAND,
    getValue: () => brand,
    onReplace: (value) => {
      onChangeBrand({ name: 'brand', value });
    },
  });

  const seoTitleFieldState = useAiGeneratedValues<LanguageInterface>(
    AI_FIELDS.PRODUCT_SEO_TITLE,
  );

  const seoDescriptionFieldState = useAiGeneratedValues<LanguageInterface>(
    AI_FIELDS.PRODUCT_SEO_DESCRIPTION,
  );

  const seoUrlFieldState = useAiGeneratedValues<LanguageInterface>(
    AI_FIELDS.PRODUCT_SEO_URL,
  );

  const tagsFieldState = useAiGeneratedValues<string[]>(AI_FIELDS.PRODUCT_TAGS);

  const brandFieldState = useAiGeneratedValues<string>(AI_FIELDS.PRODUCT_BRAND);

  const trackingFeatureProps = {
    feature: AiFeature.AI_PRODUCT_SEO_BRAND_TAGS,
  } as const;

  const {
    tooltipText,
    hasNameAndDescription,
    handleClickGenerateWithAI,
    setIsGeneratedAI,
    generateAllWithAI,
    isGeneratedAI,
    isGeneratingAI,
  } = useTagsAndSeoIA({
    name,
    description,
    tags,
    brand,
    seoTitle,
    seoDescription,
    onChangeTags,
    onChangeBrand,
    onChangeSeo,
    openModal,
  });

  const handleChangeTags: onChangeTagsType = (tags) => {
    onChangeTags(tags);
    tagsFieldState.resetAiGeneratedValues({
      handler: (prevAiTags: string[]) => {
        if (!prevAiTags) return prevAiTags;
        return prevAiTags.filter((aiTag) => tags.value.includes(aiTag));
      },
    });

    if (
      !isGeneratedAI.tags.every((tag) => tags.value.includes(tag)) &&
      !isSuggestingTagsDismissAI.current
    ) {
      isSuggestingTagsDismissAI.current = true;
      trackingAiGenerateTagsEdit(trackingFeatureProps);
    }
    setIsGeneratedAI((prev) => {
      const aiGeneratedTags = prev.tags.filter((tag) =>
        tags.value.includes(tag),
      );
      return {
        ...prev,
        tags: aiGeneratedTags,
      };
    });
  };

  const handleChangeBrand: onChangeBrandType = (brand) => {
    onChangeBrand(brand);
    if (isGeneratedAI.brand === true) {
      trackingAiGenerateBrandEdit(trackingFeatureProps);
    }
    setIsGeneratedAI((prev) => ({ ...prev, brand: false }));

    brandFieldState.resetAiGeneratedValues();
  };

  const handleChangeSeo: onChangeSeoType = (seo) => {
    onChangeSeo(seo);
    const { name, language } = seo;

    if (name === 'seoTitle') {
      seoTitleFieldState.resetAiGeneratedValues();
    } else if (name === 'seoDescription') {
      seoDescriptionFieldState.resetAiGeneratedValues();
    } else if (name === 'seoUrl') {
      seoUrlFieldState.resetAiGeneratedValues();
    }

    if (!language) return;
    if (name !== 'seoTitle' && name !== 'seoDescription') return;
    if (name === 'seoTitle' && isGeneratedAI.seoTitle[language] === true) {
      trackingAiGenerateSeoTitleEdit(trackingFeatureProps, language);
    }
    if (
      name === 'seoDescription' &&
      isGeneratedAI.seoDescription[language] === true
    ) {
      trackingAiGenerateSeoDescriptionEdit(trackingFeatureProps, language);
    }
    setIsGeneratedAI((prev) => ({
      ...prev,
      [name]: { ...prev[name], [language]: false },
    }));
  };

  const generateWithAiLink = (
    <GenerateWithAiLink
      trackingFeature={AiFeature.AI_PRODUCT_SEO_BRAND_TAGS}
      fontSize="caption"
      onClick={() => {
        isSuggestingTagsDismissAI.current = false;
        handleClickGenerateWithAI();
      }}
      tooltipText={tooltipText}
      disabled={!!tooltipText}
    />
  );

  const renderContent = (
    <Box display="flex" flexDirection="column" gap="4">
      <AllTags
        tags={tags}
        aiGenerated={[
          ...isGeneratedAI.tags,
          ...(tagsFieldState.aiGeneratedValues || []),
        ]}
        disabled={isGeneratingAI.tags}
        missingNameAndDescription={!hasNameAndDescription}
        onChange={handleChangeTags}
        onClickGenerateWithAI={() => {
          isSuggestingTagsDismissAI.current = false;
          generateAllWithAI('tags');
        }}
        aiNavigationAttributes={tagsField.navigationAttributes}
      />
      <Brand
        value={brand}
        disabled={isGeneratingAI.brand}
        aiGenerated={isGeneratedAI.brand || brandFieldState.isAiGenerated}
        missingNameAndDescription={!hasNameAndDescription}
        onChange={handleChangeBrand}
        onClickGenerateWithAI={generateAllWithAI}
        aiNavigationAttributes={brandField.navigationAttributes}
      />
      <Seo
        name={name}
        description={description}
        seoTitle={seoTitle}
        seoTitleAI={{
          isGeneratingAI: isGeneratingAI.seo,
          isGeneratedAI: {
            ...isGeneratedAI.seoTitle,
            ...(seoTitleFieldState.aiGeneratedValues as LanguageInterfaceBoolean),
          },
        }}
        seoDescription={seoDescription}
        seoDescriptionAI={{
          isGeneratingAI: isGeneratingAI.seo,
          isGeneratedAI: {
            ...isGeneratedAI.seoDescription,
            ...(seoDescriptionFieldState.aiGeneratedValues as LanguageInterfaceBoolean),
          },
        }}
        missingNameAndDescription={!hasNameAndDescription}
        onClickGenerateWithAI={generateAllWithAI}
        onChangeSeo={handleChangeSeo}
        seoUrl={seoUrl}
        seoUrlAI={seoUrlFieldState.isAiGenerated}
        aiNavigationAttributes={{
          title: seoTitleField.navigationAttributes,
          description: seoDescriptionField.navigationAttributes,
          url: seoUrlField.navigationAttributes,
        }}
      />
    </Box>
  );

  useEffect(() => {
    if (!autoOpen || hasAutoOpenedRef.current) return;
    const element = document.getElementById('tags-and-seo-card');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => openModal(), 600);
      hasAutoOpenedRef.current = true;
    }
  }, [autoOpen, openModal]);

  return hasCatalogProductHashedSlug ? (
    <Card>
      <Card.Header>
        <Box display="flex" gap="2" alignItems="center">
          <Title as="h4">{t('products.detail.seo.card.title')}</Title>
          {generateWithAiLink}
        </Box>
      </Card.Header>
      <Card.Body>{renderContent}</Card.Body>
    </Card>
  ) : (
    <>
      <ShowTagsAndSeoCard
        tags={tags}
        onChange={handleChangeTags}
        onOpen={openModal}
        aiGeneratedTags={[
          ...isGeneratedAI.tags,
          ...(tagsFieldState.aiGeneratedValues || []),
        ]}
        slot={generateWithAiLink}
        aiNavigationAttributes={tagsField.navigationAttributes}
      />
      <ModalAside
        isOpen={isShowModal}
        onDidDismiss={closeModal}
        headerContent={t('products.detail.tags.title')}
      >
        {renderContent}
      </ModalAside>
    </>
  );
}

export default TagsAndSeo;
